import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, ArrowRight, CheckCircle2, AlertTriangle } from 'lucide-react';
import { STUDIO } from '../data/mock';
import { usePageMeta } from '../components/Layout';
import { Reveal, PageHero, CloseCTA } from '../components/Shared';
import { sendContact } from '../lib/api';

function validateContact(form) {
  const errs = {};
  if (!form.name.trim() || form.name.trim().length < 2) errs.name = 'Please enter your name.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Please enter a valid email.';
  if (form.message.trim().length < 10) errs.message = 'Tell us a little more (10+ characters).';
  return errs;
}

function ContactInfo() {
  return (
    <div className="space-y-5">
      <div className="glass-card p-6 flex gap-5">
        <MapPin size={21} className="shrink-0 mt-1" style={{ color: 'var(--pf-brand)' }} />
        <div>
          <p className="font-display font-semibold text-white">Visit</p>
          <p className="text-muted-pf text-[15px] mt-1">{STUDIO.address}<br />{STUDIO.neighborhood}<br /><span className="text-[13.5px]">Lower 3rd level of the Travis Walk parking garage — free parking inside.</span></p>
        </div>
      </div>
      <div className="glass-card p-6 flex gap-5">
        <Clock size={21} className="shrink-0 mt-1" style={{ color: 'var(--pf-brand)' }} />
        <div>
          <p className="font-display font-semibold text-white">Hours</p>
          {STUDIO.hours.map((h) => (
            <p key={h.d} className="text-muted-pf text-[15px] mt-1"><span className="text-white">{h.d}:</span> {h.h}</p>
          ))}
        </div>
      </div>
      <div className="glass-card p-6 flex gap-5">
        <Phone size={21} className="shrink-0 mt-1" style={{ color: 'var(--pf-brand)' }} />
        <div>
          <p className="font-display font-semibold text-white">Call or Email</p>
          <p className="text-muted-pf text-[15px] mt-1">
            <a href="tel:+14693721085" className="hover:text-white transition-colors">{STUDIO.phone}</a><br />
            <a href={`mailto:${STUDIO.email}`} className="hover:text-white transition-colors">{STUDIO.email}</a>
          </p>
        </div>
      </div>
      <div className="rounded-2xl overflow-hidden border border-[rgba(148,170,220,0.16)] h-[260px]">
        <iframe
          title="Map to PITFIT — 4514 Travis St, Dallas"
          src={STUDIO.mapEmbed}
          width="100%"
          height="100%"
          style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg) contrast(0.9)' }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      </div>
    </div>
  );
}

function ContactForm({ form, setForm, errors, apiError, submitting, onSubmit }) {
  return (
    <form onSubmit={onSubmit} noValidate className="glass-card p-8 md:p-9" style={{ borderColor: 'rgba(125,167,255,0.3)' }}>
      {apiError && (
        <div className="flex items-start gap-3 rounded-lg border border-[rgba(248,113,113,0.4)] bg-[rgba(248,113,113,0.08)] px-4 py-3.5 mb-6" role="alert">
          <AlertTriangle size={18} className="shrink-0 mt-0.5 text-[#f87171]" />
          <p className="text-[#fca5a5] text-[14px]">{apiError}</p>
        </div>
      )}
      <div className="mb-6">
        <label htmlFor="c-name" className="pf-label">Full Name</label>
        <input id="c-name" type="text" className="pf-field" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoComplete="name" />
        {errors.name && <p className="text-[#f87171] text-[13px] mt-2" role="alert">{errors.name}</p>}
      </div>
      <div className="mb-6">
        <label htmlFor="c-email" className="pf-label">Email</label>
        <input id="c-email" type="email" className="pf-field" placeholder="you@email.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" />
        {errors.email && <p className="text-[#f87171] text-[13px] mt-2" role="alert">{errors.email}</p>}
      </div>
      <div className="mb-8">
        <label htmlFor="c-msg" className="pf-label">Message</label>
        <textarea id="c-msg" rows={6} className="pf-field resize-none" placeholder="Ask us anything — classes, memberships, parking, private events…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
        {errors.message && <p className="text-[#f87171] text-[13px] mt-2" role="alert">{errors.message}</p>}
      </div>
      <button type="submit" className="btn-primary w-full" disabled={submitting}>
        {submitting ? 'Sending…' : <>Send Message <ArrowRight size={16} /></>}
      </button>
    </form>
  );
}

export default function Contact() {
  usePageMeta(
    'Contact — PITFIT Dallas',
    'Contact PITFIT in Uptown Dallas: 4514 Travis St at Travis Walk. Hours, phone, email, free parking details and a map to the PIT.'
  );

  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setApiError('');
    const errs = validateContact(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    try {
      await sendContact(form);
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setApiError(typeof detail === 'string' ? detail : 'Message failed to send — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero
        tag="Get In Touch"
        title="Talk to the PIT."
        sub="Questions about classes, memberships, or whether you can really handle a non-stop class? (You can.) We answer fast."
      />

      <section className="section-pad !pt-8">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12">
          <Reveal>
            <ContactInfo />
          </Reveal>

          <Reveal delay={100}>
            {sent ? (
              <div className="glass-card p-9 text-center h-full flex flex-col items-center justify-center" style={{ borderColor: 'rgba(125,167,255,0.4)' }}>
                <CheckCircle2 size={42} style={{ color: 'var(--pf-brand2)' }} />
                <h2 className="font-display font-bold text-white text-2xl mt-5">Message received.</h2>
                <p className="text-muted-pf mt-3 text-[15.5px] max-w-sm">We'll get back to you within one business day — usually faster. Meanwhile, the fastest answer to most questions is taking a class.</p>
              </div>
            ) : (
              <ContactForm form={form} setForm={setForm} errors={errors} apiError={apiError} submitting={submitting} onSubmit={submit} />
            )}
          </Reveal>
        </div>
      </section>

      <CloseCTA />
    </>
  );
}
